import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { socialApi, type MediaAsset } from '@/lib/social-api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Check, Loader2, Upload } from 'lucide-react';

interface MediaPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  selectedAssets?: MediaAsset[];
  onConfirm: (assets: MediaAsset[]) => void;
  /** Reihenfolge zählt bei Carousels — deshalb Mehrfachauswahl mit Nummern. */
  multiple?: boolean;
}

export function MediaPicker({
  open,
  onOpenChange,
  selectedIds,
  selectedAssets,
  onConfirm,
  multiple = true,
}: MediaPickerProps) {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [picked, setPicked] = useState<string[]>(selectedIds);
  const fileInput = useRef<HTMLInputElement>(null);
  const wasOpen = useRef(false);
  const availableAssets = useMemo(() => Array.from(new Map(
    [...(selectedAssets ?? []), ...assets].map((asset) => [asset.id, asset])
  ).values()), [selectedAssets, assets]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setAssets(await socialApi.listMedia());
    } catch {
      setLoadError(true);
      toast.error('Medien konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setPicked(multiple ? selectedIds : selectedIds.slice(0, 1));
      void load();
    }
    wasOpen.current = open;
  }, [open, selectedIds, multiple, load]);

  const toggle = (id: string) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return multiple ? [...prev, id] : [id];
    });
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length || uploading) return;
    setUploading(true);
    try {
      const uploaded: MediaAsset[] = [];
      for (const file of Array.from(files).slice(0, multiple ? undefined : 1)) {
        try {
          uploaded.push(await socialApi.uploadMedia(file));
        } catch (e) {
          toast.error(`${file.name}: ${e instanceof Error ? e.message : 'Upload fehlgeschlagen.'}`);
        }
      }
      if (uploaded.length) {
        setAssets((previous) => [...uploaded, ...previous.filter((asset) => !uploaded.some((item) => item.id === asset.id))]);
        setPicked((prev) => multiple ? [...new Set([...prev, ...uploaded.map((asset) => asset.id)])] : uploaded.slice(0, 1).map((asset) => asset.id));
        toast.success(`${uploaded.length} Medium/Medien hochgeladen.`);
      }
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const confirm = () => {
    if (loading || uploading || loadError) return;
    const byId = new Map(availableAssets.map((a) => [a.id, a]));
    if (picked.some((id) => !byId.has(id))) {
      toast.error('Ausgewählte Medien konnten nicht geladen werden. Bitte erneut öffnen.');
      return;
    }
    onConfirm(picked.map((id) => byId.get(id)).filter((a): a is MediaAsset => Boolean(a)));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Medien wählen</DialogTitle>
          <DialogDescription>
            Instagram, TikTok und YouTube brauchen öffentlich erreichbare Dateien — lade sie hier hoch,
            statt externe Links einzufügen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            aria-label="Medien hochladen"
            multiple={multiple}
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
            className="hidden"
            onChange={(e) => void upload(e.target.files)}
          />
          <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={loading || uploading}>
            {uploading ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <Upload size={14} className="mr-1.5" />
            )}
            Hochladen
          </Button>
          <p className="text-xs text-muted">{picked.length} ausgewählt</p>
        </div>

        <div className="max-h-[45vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-foreground" />
            </div>
          ) : loadError ? (
            <div role="alert" className="py-8 text-center text-sm text-muted">
              <p>Medien konnten nicht geladen werden.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>Erneut laden</Button>
            </div>
          ) : availableAssets.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted">
              Noch keine Medien in der Bibliothek.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {availableAssets.map((asset) => {
                const index = picked.indexOf(asset.id);
                const isPicked = index >= 0;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    aria-label={asset.original_filename || `Medium ${asset.id}`}
                    aria-pressed={isPicked}
                    onClick={() => toggle(asset.id)}
                    className={cn(
                      'relative overflow-hidden rounded-lg border-2 transition',
                      isPicked ? 'border-border' : 'border-transparent hover:border-border'
                    )}
                  >
                    {asset.mime_type.startsWith('video/') ? (
                      <video src={asset.public_url || undefined} className="aspect-square w-full object-cover" muted />
                    ) : (
                      <img
                        src={asset.public_url || undefined}
                        alt={asset.original_filename || ''}
                        className="aspect-square w-full object-cover"
                      />
                    )}
                    {isPicked && (
                      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                        {multiple ? index + 1 : <Check size={12} />}
                      </span>
                    )}
                    {!asset.public_url && (
                      <span className="absolute inset-x-0 bottom-0 bg-warning/90 px-1 py-0.5 text-[9px] text-warning-foreground">
                        keine öffentliche URL
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={confirm} disabled={loading || uploading || loadError}>Übernehmen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
